// Every chart in this tree must route its ticks, tooltip, legend and aria
// description through these helpers so `masked === true` blanks the numbers
// everywhere at once (§7.2/WS-1 step 2 of the 2026-09-06 UX execution plan).
// A `null` point value is NEVER coerced to `0` here -- it is a gap, and the
// caller must skip it in a path/line rather than plotting a zero.
import { formatMoney, maskAmount } from '@/lib/money';

export interface ChartPoint {
  date: string;
  value: string | null;
  kind: 'actual' | 'carried' | 'gap';
}

/** Formats one point's value for a tick/legend, honouring the mask flag. Never renders a gap as `0`. */
export function maskTick(value: string | null, masked: boolean, currency = 'USD'): string {
  if (value === null) return '';
  return formatMoney(value, { currency, masked });
}

/** Formats a full tooltip line for one point, including its honest kind. */
export function maskTooltip(point: ChartPoint, masked: boolean, currency = 'USD'): string {
  if (point.value === null || point.kind === 'gap') {
    return `${point.date}: no snapshot`;
  }
  const amount = formatMoney(point.value, { currency, masked });
  const suffix = point.kind === 'carried' ? ' (carried forward)' : '';
  return `${point.date}: ${amount}${suffix}`;
}

/** Masks an already-formatted string for tick/legend reuse (re-exported for chart call sites). */
export { maskAmount };

/** Applies masking across a whole series' worth of formatted values, e.g. for a legend list. */
export function maskSeries(points: ChartPoint[], masked: boolean, currency = 'USD'): string[] {
  return points.map((p) => maskTooltip(p, masked, currency));
}

/**
 * A visually-hidden `<table>` describing the series, so the data (masked
 * identically to the visible chart) is reachable without sight (§7 step 4).
 */
export function ariaTableFor(points: ChartPoint[], masked: boolean, currency = 'USD') {
  return {
    caption: 'Chart data',
    rows: points.map((p) => ({
      date: p.date,
      value: p.value === null || p.kind === 'gap' ? 'no snapshot' : formatMoney(p.value, { currency, masked }),
      kind: p.kind,
    })),
  };
}
